import type { ChartPoint, ChartShadingRegion } from "@assetwatch/charts";
import { z } from "zod";
import config from "../../config";
import type {
  CmeAsset,
  CmeFeedbackCost,
  FeatureSeriesResponse,
  FeedbackVerdict,
} from "./api";

/** Longest history `feature_series` will return. Year and All both use it. */
export const MAX_DAYS = 365;

/**
 * The chart's history control, in the same order as AssetWatch Web.
 * Hour still fetches one day, then the chart opens on the last hour, because
 * the API only accepts whole days.
 */
export const HISTORY_RANGES = [
  { id: "hour", label: "Hour", days: 1 },
  { id: "day", label: "Day", days: 1 },
  { id: "week", label: "Week", days: 7 },
  { id: "month", label: "Month", days: 30 },
  { id: "3-month", label: "3 Month", days: 90 },
  { id: "year", label: "Year", days: MAX_DAYS },
  { id: "all", label: "All", days: MAX_DAYS },
] as const;

export type HistoryRangeId = (typeof HISTORY_RANGES)[number]["id"];

export const DEFAULT_HISTORY: HistoryRangeId = "3-month";

const HISTORY_IDS = HISTORY_RANGES.map((range) => range.id) as [
  HistoryRangeId,
  ...HistoryRangeId[],
];

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * The page's URL state, so a link opens the same chart for anyone with access.
 * - `date`  the flex slow-trend day, yyyy-MM-dd. The route fills in today when it's missing.
 * - `cme_user_id` the facility CME's Users.UserID. It filters the list. The name is not in the URL.
 * - `item`  the asset row that's open.
 * - `feature` the graphed feature id (e.g. `RpmBanding:e1`); unset graphs the first
 * - `history` which range the chart opens on (Hour through All)
 * - `x_start`/`x_stop` an X window in Unix seconds to open zoomed into
 *
 * Invalid values fall back to undefined rather than throwing.
 */
export const cmeFeedbackSearchSchema = z.object({
  date: z.string().regex(DATE_RE).optional().catch(undefined),
  cme_user_id: z.coerce.number().int().positive().optional().catch(undefined),
  item: z.string().trim().min(1).optional().catch(undefined),
  feature: z.string().trim().min(1).max(200).optional().catch(undefined),
  history: z.enum(HISTORY_IDS).optional().catch(undefined),
  x_start: z.coerce.number().int().optional().catch(undefined),
  x_stop: z.coerce.number().int().optional().catch(undefined),
});

/**
 * Search for this route. `cme` is always cleared: the router merges this result
 * onto the current query, and a missing key would leave a person's name in the URL.
 */
export function parseCmeFeedbackSearch(search: unknown) {
  return {
    ...cmeFeedbackSearchSchema.parse(search),
    cme: undefined,
  };
}

/** The selected history range, or 3 Month when the URL doesn't name one. */
export function historyRange(
  id: string | undefined,
): (typeof HISTORY_RANGES)[number] {
  return (
    HISTORY_RANGES.find((range) => range.id === id) ??
    HISTORY_RANGES.find((range) => range.id === DEFAULT_HISTORY) ??
    HISTORY_RANGES[0]
  );
}

/**
 * Hour opens zoomed to the last hour before the detection. The other ranges
 * show the whole fetched window.
 */
export function historyViewport(
  id: HistoryRangeId,
  detectedAt: number,
): { min: number; max: number } | undefined {
  if (id !== "hour") {
    return undefined;
  }
  return { min: detectedAt - 3600, max: detectedAt };
}

/** One calendar day for the Fault 41 query, or null until the user picks a date. */
export function selectedDay(
  date: string | undefined,
): { start: string; end: string } | null {
  if (!date) {
    return null;
  }
  return { start: date, end: date };
}

/** A yyyy-MM-dd URL value as a local calendar date for the date picker. */
export function calendarDateFromParam(value: string): Date {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day);
}

/** A date picker's local calendar date as yyyy-MM-dd. */
export function calendarDateParam(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

/**
 * A point is checked off only when its feedback exists: a review is saved,
 * every feature is rated (so it has a verdict), and there is a comment.
 */
export function feedbackComplete({
  stored,
  verdict,
  comment,
}: {
  stored: boolean;
  verdict: FeedbackVerdict | null;
  comment: string | null | undefined;
}): boolean {
  return stored && verdict !== null && (comment?.trim().length ?? 0) > 0;
}

/**
 * The verdict a point shows in the rail and that Save sends. Once the point's
 * ratings have been edited on this page, only those count: falling back to the
 * stored verdict would let Save send partial ratings over a full saved review.
 */
export function railVerdict(
  fromFeatures: FeedbackVerdict | null,
  edited: boolean,
  stored: FeedbackVerdict | null,
): FeedbackVerdict | null {
  return edited ? fromFeatures : (fromFeatures ?? stored);
}

/**
 * Saved feedback is locked: every point on the machine has a stored review,
 * and the machine isn't the one unlocked with Edit. A machine with no points
 * is never locked.
 */
export function machineLocked(
  pointIds: readonly string[],
  storedIds: ReadonlySet<string>,
  machineKey: string | undefined,
  editingMachine: string | null,
): boolean {
  return (
    pointIds.length > 0 &&
    pointIds.every((id) => storedIds.has(id)) &&
    machineKey !== editingMachine
  );
}

/** Athena scan figures as one or more queries reported them. */
export type QueryCost = {
  usd: number | null;
  mb_scanned: number | null;
  rows: number | null;
  seconds: number | null;
  queries: number;
};

/**
 * Adds up several queries' scans. A figure any query didn't report stays null:
 * a partial sum would read as the whole cost. Seconds is the slowest query,
 * since they run side by side. Null when there's nothing to report.
 */
export function combineQueryCosts(
  costs: readonly (CmeFeedbackCost | null | undefined)[],
): QueryCost | null {
  // No query reported a cost at all: nothing to show, rather than an
  // all-"unavailable" line.
  if (costs.every((cost) => cost === null || cost === undefined)) {
    return null;
  }
  const sum = (pick: (cost: CmeFeedbackCost) => number | null | undefined) => {
    let total = 0;
    for (const cost of costs) {
      const value = cost ? pick(cost) : null;
      if (value === null || value === undefined) {
        return null;
      }
      total += value;
    }
    return total;
  };
  const seconds = costs.map((cost) => cost?.seconds);
  return {
    usd: sum((cost) => cost.usd),
    mb_scanned: sum((cost) => cost.mb_scanned),
    rows: sum((cost) => cost.rows),
    seconds: seconds.every((value) => typeof value === "number")
      ? Math.max(...(seconds as number[]))
      : null,
    queries: costs.length,
  };
}

/**
 * Format a scan cost. Sub-hundredth-of-a-cent queries are common, and
 * `toFixed(4)` renders those as "$0.0000", which reads as free rather than
 * cheap, so anything below the displayable floor is shown as a bound.
 */
export function formatQueryUsd(usd: number): string {
  if (usd > 0 && usd < 0.0001) {
    return "<$0.0001";
  }
  return `$${usd.toFixed(4)}`;
}

/** The parts of a "Scan estimate" line. Unknown figures say so rather than $0. */
export function queryCostParts(cost: QueryCost): string[] {
  return [
    cost.usd === null ? "cost unavailable" : formatQueryUsd(cost.usd),
    cost.mb_scanned === null
      ? "MB unavailable"
      : `${cost.mb_scanned.toLocaleString(undefined, {
          maximumFractionDigits: 1,
        })} MB scanned`,
    cost.queries <= 1 ? null : `${cost.queries} queries`,
    cost.rows === null ? null : `${cost.rows.toLocaleString()} rows`,
    cost.seconds === null ? null : `${cost.seconds.toFixed(2)}s`,
  ].filter((part): part is string => part !== null);
}

/** A yyyy-MM-dd day moved by whole calendar days, e.g. -1 for the day before. */
export function shiftDateParam(value: string, days: number): string {
  const date = calendarDateFromParam(value);
  date.setDate(date.getDate() + days);
  return calendarDateParam(date);
}

/**
 * The date to put in a URL that has none: the viewer's local today. Null when
 * the URL already names a date, so a shared link still opens its own day.
 */
export function missingDateDefault(
  date: string | undefined,
  now: Date = new Date(),
): string | null {
  return date ? null : calendarDateParam(now);
}

/**
 * The X window from the URL, if both bounds are present and ordered. A partial
 * or inverted pair is ignored so a hand-edited URL can't wedge the chart into
 * an empty window.
 */
export function parseViewport(
  xStart: number | undefined,
  xStop: number | undefined,
): { min: number; max: number } | undefined {
  if (xStart === undefined || xStop === undefined || xStop <= xStart) {
    return undefined;
  }
  return { min: xStart, max: xStop };
}

type MachineGroupItem = {
  machine: string;
  machine_id: number | null;
  facility: string;
};

export type MachineGroup<T> = {
  key: string;
  machine: string;
  items: T[];
};

/**
 * Fault rows for one CME, gathered by machine. `machine_id` is the group;
 * a missing id falls back to facility plus name so two unnamed machines stay
 * apart. Order is first appearance, which is newest detection first.
 */
export function groupAssetsByMachine<T extends MachineGroupItem>(
  items: T[],
): MachineGroup<T>[] {
  const groups: MachineGroup<T>[] = [];
  const indexByKey = new Map<string, number>();

  for (const item of items) {
    const key =
      item.machine_id === null
        ? `name:${item.facility}:${item.machine}`
        : `id:${item.machine_id}`;
    const existing = indexByKey.get(key);
    if (existing === undefined) {
      indexByKey.set(key, groups.length);
      groups.push({
        key,
        machine: item.machine || "Unknown machine",
        items: [item],
      });
    } else {
      groups[existing].items.push(item);
    }
  }

  return groups;
}

/** The machine's comment. Monitoring points do not keep their own. */
export function machineComment(
  points: readonly { comment: string | null | undefined }[],
): string {
  for (const point of points) {
    const comment = point.comment?.trim() ?? "";
    if (comment.length > 0) {
      return comment;
    }
  }
  return "";
}

/** Brand when a machine has one monitoring point; light orange when it has more. */
export function monitoringPointCount(
  items: { monitoring_point_id: string }[],
): number {
  return new Set(items.map((item) => item.monitoring_point_id)).size;
}

export function machineStripClass(pointCount: number): string {
  return pointCount > 1 ? "bg-warning-container" : "bg-primary";
}

/**
 * AssetWatch Web origin for this deploy. Dev matches the host the facility app
 * already links to. Prod is the customer app.
 */
export function assetWatchWebOrigin(deployEnvironment: string): string {
  if (deployEnvironment === "prod") {
    return "https://app.assetwatch.com";
  }
  return `https://${deployEnvironment}.${deployEnvironment}.assetwatch.com`;
}

/**
 * The live asset page on its Analysis tab, scrolled to the monitoring point:
 * /customers/{ExternalCustomerID}/assets/{MachineID}?fid="{FacilityID}"
 *   &sidesheet-refresh="1"&asset-tab=analysis#{MonitoringPointID}
 * AssetWatch Web reads `fid` and `sidesheet-refresh` as JSON strings, hence the
 * quotes. The `#` is the monitoring point card it scrolls to; it's left off
 * when the API didn't send that id. Null when the customer, machine or
 * facility id is missing.
 */
export function assetPageUrl(
  asset: Pick<
    CmeAsset,
    | "external_customer_id"
    | "machine_id"
    | "facility_id"
    | "monitoring_point_db_id"
  >,
  origin = assetWatchWebOrigin(config.deployEnvironment),
): string | null {
  if (
    asset.external_customer_id === null ||
    asset.machine_id === null ||
    asset.facility_id === null
  ) {
    return null;
  }
  const params = new URLSearchParams({
    fid: JSON.stringify(String(asset.facility_id)),
    "sidesheet-refresh": JSON.stringify("1"),
    "asset-tab": "analysis",
  });
  const hash =
    asset.monitoring_point_db_id === null
      ? ""
      : `#${asset.monitoring_point_db_id}`;
  return `${origin}/customers/${encodeURIComponent(asset.external_customer_id)}/assets/${asset.machine_id}?${params}${hash}`;
}

export const VERDICT_OPTIONS: { value: FeedbackVerdict; label: string }[] = [
  { value: "useful", label: "Useful" },
  { value: "not_useful", label: "Not useful" },
];

export function verdictLabel(verdict: FeedbackVerdict | null): string {
  return (
    VERDICT_OPTIONS.find((option) => option.value === verdict)?.label ??
    "Pending"
  );
}

export function verdictTone(
  verdict: FeedbackVerdict | null,
): "positive" | "negative" {
  if (verdict === "useful") {
    return "positive";
  }
  return "negative";
}

/** Save stays off until every monitoring point on the machine has every feature rated. */
export function feedbackReadyToSave(
  points: { monitoring_point: string; verdict: FeedbackVerdict | null }[],
  labels: { cme_name: string; machine: string },
): boolean {
  return (
    labels.cme_name.trim().length > 0 &&
    labels.machine.trim().length > 0 &&
    points.length > 0 &&
    points.every(
      (point) =>
        point.monitoring_point.trim().length > 0 &&
        (point.verdict === "useful" || point.verdict === "not_useful"),
    )
  );
}

/** The Select's string value, narrowed back to a verdict. */
export function parseVerdict(value: string | null): FeedbackVerdict | null {
  return (
    VERDICT_OPTIONS.find((option) => option.value === value)?.value ?? null
  );
}

const SMALL_VALUE_FORMAT = new Intl.NumberFormat("en-US", {
  maximumSignificantDigits: 3,
});

/**
 * Hover value after the series name, e.g. `1.61g` in `X-Axis: 1.61g`.
 * Values below 1 keep three significant digits so 0.002 never reads as 0.00.
 */
export function formatChartValue(y: number, unit: string | undefined): string {
  const value =
    y !== 0 && Math.abs(y) < 1
      ? SMALL_VALUE_FORMAT.format(y)
      : y.toLocaleString("en-US", {
          maximumFractionDigits: 2,
          minimumFractionDigits: Number.isInteger(y) ? 0 : 2,
        });
  return unit ? `${value}${unit}` : value;
}

const AXIS_MAGNITUDE_FORMAT = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 6,
  useGrouping: false,
});

/**
 * Y-axis tick. Each tick keeps its own precision so neighbouring ticks never
 * collapse to the same label (0.06 and 0.08 must not both read "0.1").
 */
export function formatAxisMagnitude(value: number) {
  if (!Number.isFinite(value)) {
    return "";
  }
  return AXIS_MAGNITUDE_FORMAT.format(Number(value.toPrecision(12)));
}

/**
 * Axis tick for the same Unix seconds, in the browser's time zone like the
 * chart's hover label. Hour and day views include the time.
 */
export function formatAxisTimestamp(
  value: Date | number,
  history: HistoryRangeId = DEFAULT_HISTORY,
): string {
  const date = value instanceof Date ? value : new Date(value * 1000);
  const showTime = history === "hour" || history === "day";
  return new Intl.DateTimeFormat("en-US", {
    day: "numeric",
    month: "short",
    ...(showTime
      ? { hour: "numeric", minute: "2-digit" }
      : { year: "2-digit" }),
  }).format(date);
}

/** Chart points from the columnar series. A null stays null: the chart draws a gap. */
/**
 * Predicted 1X / RPM, in Hz: the RPM model's `selected_fr_hz` in the
 * `RpmResult` feature group (definition from #datascience-mle, 2026-10-28).
 * Fetched with the same feature-series call as the graph.
 */
export const RPM_FEATURE = {
  feature: "selected_fr_hz",
  feature_group: "RpmResult",
} as const;

/** The predicted 1X window: the median over this many days, per the definition. */
export const PREDICTED_1X_DAYS = 7;

/** RPM (Hz) by reading time in Unix seconds. RPM rows share the readings' timestamps. */
export function rpmByTimestamp(
  series: FeatureSeriesResponse | undefined,
): Map<number, number> {
  const rpm = new Map<number, number>();
  series?.timestamps.forEach((timestamp, index) => {
    const value = series.values[index];
    if (typeof value === "number" && Number.isFinite(value)) {
      rpm.set(timestamp, value);
    }
  });
  return rpm;
}

/**
 * The RPM at the hovered reading. The chart's tooltip formatter is given the
 * value and its series but not the time, so the point is found by value (the
 * first match; equal values on one series are rare, and nearby readings have
 * near-equal RPM). Null when there's no RPM at that time.
 */
export function rpmForHoveredValue(
  value: number,
  points: readonly ChartPoint[],
  rpm: ReadonlyMap<number, number>,
): number | null {
  const point = points.find((candidate) => candidate.y === value);
  if (!point) {
    return null;
  }
  const seconds =
    point.x instanceof Date ? point.x.getTime() / 1000 : Number(point.x);
  return rpm.get(seconds) ?? null;
}

/** `59.8 Hz` for the tooltip and the details box. */
export function formatRpm(hz: number): string {
  return `${hz.toFixed(1)} Hz`;
}

/**
 * Predicted 1X: the median RPM over the PREDICTED_1X_DAYS ending on `endDate`
 * (UTC days, like the queries' date partitions). Null with no readings then.
 */
export function predicted1xHz(
  rpm: ReadonlyMap<number, number>,
  endDate: string,
): number | null {
  const end = Date.parse(`${endDate}T00:00:00Z`) / 1000 + 86_400;
  const start = end - PREDICTED_1X_DAYS * 86_400;
  const values = [...rpm]
    .filter(([timestamp]) => timestamp >= start && timestamp < end)
    .map(([, hz]) => hz)
    .sort((a, b) => a - b);
  if (values.length === 0) {
    return null;
  }
  const middle = Math.floor(values.length / 2);
  return values.length % 2 === 1
    ? values[middle]
    : (values[middle - 1] + values[middle]) / 2;
}

export function seriesPoints(series: FeatureSeriesResponse): ChartPoint[] {
  return series.timestamps.map((timestamp, index) => ({
    x: new Date(timestamp * 1000),
    y: series.values[index] ?? null,
  }));
}

export function detectedFeatureId(feature: {
  feature_group: string;
  name: string;
}) {
  return `${feature.feature_group}:${feature.name}`;
}

const FEATURE_AXES = ["x", "y", "z"] as const;
export type FeatureAxis = (typeof FEATURE_AXES)[number];

export function axisSeriesLabel(axis: FeatureAxis) {
  return `${axis.toUpperCase()}-Axis`;
}

/** Friendly names for features whose backend names read poorly. */
const FEATURE_LABELS: Record<string, string> = {
  fft_harm_amp_120: "A_120Hz",
  sc_rbpf_global_int: "rbpf_intensity",
  sc_ppf_global_int: "ppf_intensity",
  sc_mod120_global_int: "mod120_intensity",
};

/** `fft_harm_amp_120_` (from `fft_harm_amp_120_x`) and `FFT_HARM_AMP_120` both become `fft_harm_amp_120`. */
function featureKey(stem: string): string {
  return stem.toLowerCase().replace(/_+$/, "");
}

/** The name shown for a feature stem. Data matching keeps the raw stem. */
export function featureLabel(stem: string): string {
  return FEATURE_LABELS[featureKey(stem)] ?? stem.replace(/_+$/, "");
}

/** The ML API's description for a feature stem, or null when it has none. */
export function featureDescription(
  descriptions: Record<string, string> | undefined,
  stem: string,
): string | null {
  return descriptions?.[featureKey(stem)] ?? null;
}

/**
 * A description's first paragraph, e.g. "Velocity spectrum energy from 1x-10x
 * running speed", without the worked example that follows. Null when there's
 * no description.
 */
export function featureSummary(
  descriptions: Record<string, string> | undefined,
  stem: string,
): string | null {
  const summary = featureDescription(descriptions, stem)
    ?.split(/\n\s*\n/)[0]
    ?.trim();
  return summary ? summary : null;
}

/** `a5y` is feature `a5` on the Y axis. A name with no axis suffix stays whole. */
export function splitFeatureName(name: string): {
  stem: string;
  axis: FeatureAxis | null;
} {
  const axis = name.slice(-1).toLowerCase();
  if (name.length > 1 && (axis === "x" || axis === "y" || axis === "z")) {
    return { stem: name.slice(0, -1), axis };
  }
  return { stem: name, axis: null };
}

export type ChartFeatureStem = {
  id: string;
  stem: string;
  feature_group: string;
  axial: boolean;
  /** Set when this stem is the highest-severity detection. */
  extremeAxis: FeatureAxis | null;
};

/**
 * One radio per feature name. Axes of the same name collapse together, and the
 * highest-severity feature's name is first.
 */
export function chartFeatureStems(
  asset: Pick<
    CmeAsset,
    "features" | "severity_feature" | "severity_feature_group"
  >,
): ChartFeatureStem[] {
  const extreme = splitFeatureName(asset.severity_feature);
  const extremeId = `${asset.severity_feature_group}:${extreme.stem}`;
  const stems: ChartFeatureStem[] = [];
  const seen = new Set<string>();

  for (const feature of chartFeatures(asset)) {
    const parsed = splitFeatureName(feature.name);
    const id = `${feature.feature_group}:${parsed.stem}`;
    if (seen.has(id)) {
      continue;
    }
    seen.add(id);
    stems.push({
      id,
      stem: parsed.stem,
      feature_group: feature.feature_group,
      axial: parsed.axis !== null,
      extremeAxis: id === extremeId ? extreme.axis : null,
    });
  }

  return stems;
}

/** One useful or not-useful choice per feature stem, keyed by point id then feature id. */
export type FeatureVerdicts = Record<string, Record<string, FeedbackVerdict>>;

/**
 * Checking a box selects it. Checking the box that is already selected clears it.
 */
export function nextFeatureVerdict(
  current: FeedbackVerdict | null,
  choice: FeedbackVerdict,
  checked: boolean,
): FeedbackVerdict | null {
  if (checked) {
    return choice;
  }
  return current === choice ? null : current;
}

export function setFeatureVerdict(
  verdicts: FeatureVerdicts,
  assetId: string,
  featureId: string,
  verdict: FeedbackVerdict | null,
): FeatureVerdicts {
  const point = { ...(verdicts[assetId] ?? {}) };
  if (verdict === null) {
    delete point[featureId];
  } else {
    point[featureId] = verdict;
  }
  const next = { ...verdicts };
  if (Object.keys(point).length === 0) {
    delete next[assetId];
    return next;
  }
  next[assetId] = point;
  return next;
}

/**
 * Null until every feature on the point is rated. A finished point is useful
 * when any feature is useful, otherwise not useful.
 */
export function pointVerdictFromFeatures(
  asset: Pick<
    CmeAsset,
    "features" | "severity_feature" | "severity_feature_group"
  >,
  verdicts: Record<string, FeedbackVerdict> | undefined,
): FeedbackVerdict | null {
  const stems = chartFeatureStems(asset);
  const values = stems.map((stem) => verdicts?.[stem.id]);
  if (stems.length === 0 || values.some((value) => value === undefined)) {
    return null;
  }
  return values.includes("useful") ? "useful" : "not_useful";
}

export type RatedFeature = {
  id: string;
  featureGroup: string;
  label: string;
  verdict: FeedbackVerdict;
};

/** Features on this point that already have a choice, in the same order as the radios. */
export function ratedFeaturesForPoint(
  asset: Pick<
    CmeAsset,
    "features" | "severity_feature" | "severity_feature_group"
  >,
  verdicts: Record<string, FeedbackVerdict> | undefined,
): RatedFeature[] {
  if (!verdicts) {
    return [];
  }
  return chartFeatureStems(asset).flatMap((stem) => {
    const verdict = verdicts[stem.id];
    if (verdict !== "useful" && verdict !== "not_useful") {
      return [];
    }
    return [
      {
        id: stem.id,
        featureGroup: stem.feature_group,
        label: `${stem.feature_group} · ${featureLabel(stem.stem)}`,
        verdict,
      },
    ];
  });
}

export type FeatureFeedback = {
  feature_group: string;
  feature: string;
  verdict: FeedbackVerdict;
};

/** Rated features as saved, by backend name. One entry covers every axis of a feature. */
export function featureFeedbackForPoint(
  asset: Pick<
    CmeAsset,
    "features" | "severity_feature" | "severity_feature_group"
  >,
  verdicts: Record<string, FeedbackVerdict> | undefined,
): FeatureFeedback[] {
  if (!verdicts) {
    return [];
  }
  return chartFeatureStems(asset).flatMap((stem) => {
    const verdict = verdicts[stem.id];
    if (verdict !== "useful" && verdict !== "not_useful") {
      return [];
    }
    return [
      {
        feature_group: stem.feature_group,
        feature: stem.stem.replace(/_+$/, ""),
        verdict,
      },
    ];
  });
}

/** The point's saved feature verdicts, keyed like the radios (`group:stem`). */
export function savedFeatureVerdicts(
  asset: Pick<
    CmeAsset,
    | "features"
    | "severity_feature"
    | "severity_feature_group"
    | "feature_verdicts"
  >,
): Record<string, FeedbackVerdict> {
  const verdicts: Record<string, FeedbackVerdict> = {};
  for (const stem of chartFeatureStems(asset)) {
    const saved = asset.feature_verdicts.find(
      (feature) =>
        feature.feature_group === stem.feature_group &&
        feature.feature === stem.stem.replace(/_+$/, ""),
    );
    if (saved) {
      verdicts[stem.id] = saved.verdict;
    }
  }
  return verdicts;
}

/** X, Y, and Z in axis order. Line color is pinned to the axis, not the sort position. */
export function stemSeriesFeatures(stem: ChartFeatureStem) {
  if (!stem.axial) {
    return [
      {
        name: stem.stem,
        feature_group: stem.feature_group,
        axis: null as FeatureAxis | null,
      },
    ];
  }

  return FEATURE_AXES.map((axis) => ({
    name: `${stem.stem}${axis}`,
    feature_group: stem.feature_group,
    axis,
  }));
}

/** Axes of this stem that were detected on the point. Every axis when none match, so the chart is never empty. */
export function detectedAxisIds(
  asset: Pick<
    CmeAsset,
    "features" | "severity_feature" | "severity_feature_group"
  >,
  stem: ChartFeatureStem,
): string[] {
  const detected = new Set(
    chartFeatures(asset)
      .filter((feature) => feature.feature_group === stem.feature_group)
      .map((feature) => feature.name.toLowerCase()),
  );
  const axes = stemSeriesFeatures(stem);
  const ids = axes
    .filter((axis) => detected.has(axis.name.toLowerCase()))
    .map((axis) => axis.axis ?? axis.name);
  return ids.length > 0 ? ids : axes.map((axis) => axis.axis ?? axis.name);
}

/**
 * Features the chart can plot. The highest-severity feature is first so it is
 * the one checked when the point opens.
 */
export function chartFeatures(
  asset: Pick<
    CmeAsset,
    "features" | "severity_feature" | "severity_feature_group"
  >,
) {
  const extreme =
    asset.severity_feature && asset.severity_feature_group
      ? [
          {
            name: asset.severity_feature,
            feature_group: asset.severity_feature_group,
          },
        ]
      : [];
  const rest = (asset.features ?? []).filter(
    (feature) =>
      feature.name !== asset.severity_feature ||
      feature.feature_group !== asset.severity_feature_group,
  );
  const seen = new Set<string>();
  return [...extreme, ...rest].filter((feature) => {
    if (!feature.name || !feature.feature_group) {
      return false;
    }
    const id = detectedFeatureId(feature);
    if (seen.has(id)) {
      return false;
    }
    seen.add(id);
    return true;
  });
}

type ShadedTrend = {
  id: string;
  trend_start: number | null;
  detected_at: number;
};

/** axis_trend_type → the AssetWatch tooltip token and axis, e.g. y_peak → PEAK (Y). */
const FEATURE_DISPLAY: Record<string, { token: string; axis: string }> = {
  x_rms: { token: "RMS", axis: "X" },
  y_rms: { token: "RMS", axis: "Y" },
  z_rms: { token: "RMS", axis: "Z" },
  x_peak: { token: "PEAK", axis: "X" },
  y_peak: { token: "PEAK", axis: "Y" },
  z_peak: { token: "PEAK", axis: "Z" },
  x_p2p: { token: "P2P", axis: "X" },
  y_p2p: { token: "P2P", axis: "Y" },
  z_p2p: { token: "P2P", axis: "Z" },
  temp: { token: "TEMP", axis: "" },
};

/** `2026-09-26 PEAK (Y): 60 days`, the slow-trend tooltip on AssetWatch Web. */
export function slowTrendTooltip(
  feature: string,
  detectedDate: string,
  days: number | null,
): string {
  const display = FEATURE_DISPLAY[feature];
  const token = display?.token ?? feature;
  const axis = display?.axis ? ` (${display.axis})` : "";
  const duration = days === null ? "" : `: ${days} days`;
  return `${detectedDate} ${token}${axis}${duration}`;
}

/** The red slow-trend chip, e.g. `SLOW TREND: PEAK`. */
export function slowTrendChip(feature: string): string {
  const token = FEATURE_DISPLAY[feature]?.token;
  return token ? `SLOW TREND: ${token}` : "SLOW TREND";
}

/**
 * Two sentences on what the detected feature means, so a CME can read the
 * shaded window. Temperature and unknown features have no glossary entry.
 */
const FEATURE_MEANING: Record<string, (axis: string) => string> = {
  PEAK: (axis) =>
    `Peak is the highest acceleration in each reading${axis}. A rising slow trend means those peaks are getting larger across the shaded window, which usually means impacts are growing.`,
  RMS: (axis) =>
    `RMS is the overall vibration energy in each reading${axis}. A rising slow trend means that energy has been climbing across the shaded window, which usually means a fault is adding vibration through the whole signal.`,
  P2P: (axis) =>
    `Peak-to-peak is the span from the lowest to the highest acceleration in each reading${axis}. A rising slow trend means that span is widening across the shaded window, which can mean growing impacts or looseness.`,
};

/** Caption under the slow-trend chip, or null when this feature has no glossary. */
export function slowTrendDescription(feature: string): string | null {
  const display = FEATURE_DISPLAY[feature];
  const meaning = display ? FEATURE_MEANING[display.token] : undefined;
  if (!display || !meaning) {
    return null;
  }
  const axis = display.axis ? ` on the ${display.axis} axis` : "";
  return meaning(axis);
}

/** The shortest trend bucket for a feature, from `e4z: 45, e4z: 60, ev3y: 30`. */
export function minimumTrendDays(
  featureTrendDays: string,
  stem: string,
): number | null {
  const days = featureTrendDays.split(",").flatMap((part) => {
    const [rawName, rawDays] = part.split(":").map((piece) => piece.trim());
    if (!rawName || splitFeatureName(rawName).stem !== stem) {
      return [];
    }
    const value = Number(rawDays);
    return Number.isFinite(value) ? [value] : [];
  });
  if (days.length === 0) {
    return null;
  }
  return Math.min(...days);
}

/**
 * Yellow window ending on the detection date. The width is the feature's
 * shortest trend bucket.
 */
export function featureTrendShading(
  id: string,
  detectedDate: string,
  days: number | null,
): ChartShadingRegion[] {
  if (days === null) {
    return [];
  }
  const detectedStart = Date.parse(`${detectedDate}T00:00:00Z`);
  if (Number.isNaN(detectedStart)) {
    return [];
  }
  const end = detectedStart + 86_400_000;
  return [
    {
      id: `trend-${id}`,
      axis: "x",
      from: new Date(end - days * 86_400_000),
      to: new Date(end),
      tone: "warning",
      opacity: "medium",
    },
  ];
}

/** Whole days in the detected trend, or null when the detector gave no start. */
export function trendDays(item: ShadedTrend): number | null {
  if (item.trend_start === null) {
    return null;
  }
  return Math.round((item.detected_at - item.trend_start) / 86_400);
}

/** The detected trend window, shaded on the chart. None without a trend start. */
export function trendShading(item: ShadedTrend): ChartShadingRegion[] {
  if (item.trend_start === null) {
    return [];
  }
  return [
    {
      id: `trend-${item.id}`,
      axis: "x",
      from: new Date(item.trend_start * 1000),
      to: new Date(item.detected_at * 1000),
      tone: "warning",
      opacity: "medium",
    },
  ];
}
